/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';

interface ErrorInfo {
  status: number | null;
  title: string;
  description: string;
  icon: React.ReactNode;
}

function getErrorInfo(error: unknown): ErrorInfo {
  if (isRouteErrorResponse(error)) {
    if (error.status === 403) {
      return {
        status: 403,
        title: 'Access Denied',
        description:
          "You don't have enough permissions to access this page. Contact your admin if this persists.",
        icon: (
          <svg className="w-16 h-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
          </svg>
        ),
      };
    }
    if (error.status === 404) {
      return {
        status: 404,
        title: 'Page Not Found',
        description: "The page you're looking for doesn't exist or has been moved.",
        icon: (
          <svg className="w-16 h-16 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        ),
      };
    }
    return {
      status: error.status,
      title: 'Something Went Wrong',
      description: error.statusText || 'An unexpected error occurred. Please try again.',
      icon: (
        <svg className="w-16 h-16 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
      ),
    };
  }

  return {
    status: null,
    title: 'Something Went Wrong',
    description: 'An unexpected error occurred. Please try again later.',
    icon: (
      <svg className="w-16 h-16 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    ),
  };
}

const ErrorPage = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const { status, title, description, icon } = getErrorInfo(error);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="max-w-md w-full text-center animate-fade-in">
        {/* Logo */}
        <div className="mb-8">
          <img
            src="/sila-wordmark.png"
            alt="Sila"
            className="h-10 mx-auto object-contain opacity-60"
          />
        </div>

        {/* Icon */}
        <div className="flex justify-center mb-6">{icon}</div>

        {/* Status code */}
        {/* status && (
          <p className="text-5xl font-bold text-foreground mb-2">{status}</p>
        ) */}

        {/* Title */}
        <h1 className="text-2xl font-semibold text-foreground mb-3">{title}</h1>

        {/* Description */}
        <p className="text-text-muted mb-8 leading-relaxed">{description}</p>

        {/* Actions */}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="px-5 py-2.5 bg-primary text-white rounded-xl font-medium hover:bg-primary-hover transition-colors"
          >
            Go Home
          </button>
          <button
            onClick={() => window.location.reload()}
            className="px-5 py-2.5 bg-background-secondary text-foreground border border-border rounded-xl font-medium hover:bg-background-tertiary transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
